// Lazy Loading Handler
// - Lazy-load für Elemente mit `data-src`
// - Verwendet IntersectionObserver für Performance
// - Video-Setup für proxy loading via HTML

type LoadableElement = HTMLImageElement | HTMLIFrameElement | HTMLElement;

// Speichern der gehashten Video-Pfade
let videoSources: { desktop: Record<string, string>; mobile: Record<string, string> } = {
	desktop: {},
	mobile: {}
};

function isImage(el: Element): el is HTMLImageElement {
	return el.tagName.toLowerCase() === 'img';
}

function loadResource(el: LoadableElement): Promise<void> {
	return new Promise((resolve) => {
		if (isImage(el)) {
			const img = el as HTMLImageElement;
			const src = img.dataset.src;
			if (!src) return resolve();
			img.onload = () => img.classList.add('loaded');
			img.onerror = () => img.classList.add('error');
			img.src = src;
			delete img.dataset.src;
			return resolve();
		}

		const src = el.getAttribute('data-src');
		if (src) {
			if (el instanceof HTMLIFrameElement) {
				el.src = src;
			} else {
				el.setAttribute('style', (el.getAttribute('style') || '') + `background-image:url('${src}')`);
			}
			el.removeAttribute('data-src');
		}
		resolve();
	});
}

/**
 * Capture gehashte Video-Pfade aus HTML und src-Attribute entfernen
 * Wird bei DOMContentLoaded aufgerufen
 */
export function captureVideoSources(): void {
	const v = document.getElementById('logoVideo') as HTMLVideoElement;
	if (!v) return;

	Array.from(v.querySelectorAll('source')).forEach((source) => {
		const src = source.getAttribute('src');
		const type = source.getAttribute('type') || '';
		if (!src) return;

		const isMobile = source.getAttribute('data-mobile') === 'true';
		if (isMobile) {
			videoSources.mobile[type] = src;
		} else {
			videoSources.desktop[type] = src;
		}
		
		// src entfernen, damit Video nicht sofort geladen wird
		source.removeAttribute('src');
	});
}

/**
 * Setup Logo Video mit der richtigen Variante (mobile/desktop)
 * Wird bei window load aufgerufen
 */
export function setupLogoVideo(): void {
	const v = document.getElementById('logoVideo') as HTMLVideoElement;
	if (!v) return;

	const isMobile = window.matchMedia('(max-width:768px)').matches;
	const variant = isMobile ? videoSources.mobile : videoSources.desktop;

	Array.from(v.querySelectorAll('source')).forEach((source) => {
		const type = source.getAttribute('type') || '';
		if (variant[type]) {
			source.setAttribute('src', variant[type]);
		}
	});

	v.load();
	v.autoplay = true;
	v.play().catch(() => {});
}

export function lazyLoadImages(root: ParentNode = document): Promise<void> {
	return new Promise((resolve) => {
		const nodes = Array.from(root.querySelectorAll('[data-src]')) as Element[];
		if (!nodes.length) {
			resolve();
			return;
		}

		if ('IntersectionObserver' in window) {
			const io = new IntersectionObserver((entries, obs) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					const el = entry.target as LoadableElement;
					loadResource(el).catch(() => {});
					obs.unobserve(entry.target);
				});
			}, { root: null, threshold: 0 });

			nodes.forEach(n => io.observe(n));
			resolve(); // IntersectionObserver Setup fertig
		} else {
			// Fallback: load all immediately
			Promise.all(nodes.map(n => loadResource(n as LoadableElement)))
				.then(() => resolve())
				.catch(() => resolve());
		}
	});
}
